// external
import { useContext } from "react";
import classNames from "classnames";
import { useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";

// components
import CustomButton from "components/partials/custom-button/custom-button";
import Heading from "components/partials/heading/heading";
import Loader from "components/partials/loader/loader";
import AidResultsTable from "./aid-results-table";
import FaoNonModelData from "./fao-non-model-data";
import FaoAdmitAdjust from "./fao-admit-adjust/fao-admit-adjust";

// state management
import { FaoBuilderContext } from "context/FaoBuilderContext";
import { FaoResultsContext } from "context/FaoResultsContext";
import useNonModeledData from "./hooks/use-non-model-data";
import useAdmitAdjustmentData from "./hooks/use-admit-adjustment-data";
import useViewResults from "./hooks/use-view-results";

// assets
import Icons from "../../assets/icons";

// helpers
import {
  formatFloats,
  formatModelValues,
} from "./helpers/published-model-helpers";

// constants
import {
  TABLE_HEADERS,
  COST_OF_ATTENDANCE_TABLE_HEADERS,
} from "./constants/results-table-headers.constants";
import {
  MODEL_DETAILS,
  NAV_BAR_ITEMS,
  SUMMARY_DETAILS,
} from "./constants/view-results.constants";

/**
 * FaoViewResults Component
 *
 * This is the main results viewing page for published FAO models.
 * It provides a comprehensive view of model results including:
 * - Model metadata and summary statistics
 * - Cost of attendance and first-year estimate tables
 * - Interactive tabs for Non-Modeled Aid Results and Admit Adjustments
 * - Export and unpublish functionality
 *
 * The component handles the complete lifecycle of viewing and interacting with
 * published model results, including data loading, user interactions, and navigation.
 */
function FaoViewResults() {
  const navigate = useNavigate();

  // Get model configuration from builder context
  const { modelConfig } = useContext(FaoBuilderContext);

  // Get results data and state management from results context
  const {
    firstYearEstimate,
    costOfAttendance,
    loading,
    activeTab,
    setActiveTab,
    isSaving,
    isExporting,
  } = useContext(FaoResultsContext);

  // Initialize data loading for non-modeled data and admit adjustments
  useNonModeledData();
  useAdmitAdjustmentData();

  // Get handler functions for export and unpublish actions
  const { handleUnpublishModelHandler, handleExportModel } = useViewResults();

  // Prevent rendering until modelConfig is available to prevent flash of incomplete content
  // This ensures smooth transition from model selection to results view
  if (!modelConfig) {
    return <Loader />;
  }

  /**
   * Renders individual metadata items (Author, Last updated, etc.)
   * Formats the content using helper functions and applies appropriate styling
   */
  const renderMetadataItem = (item) => {
    const { styling, content } = formatModelValues(item, modelConfig);
    return (
      <div key={item} className="flex flex-col">
        <p className="text-sm text-gray-04">{item}</p>
        <p className={`${styling} text-sm font-medium`}>{content}</p>
      </div>
    );
  };

  /**
   * Renders summary detail cards showing key model metrics
   * (Number enrolled, Net Revenue, Total Institutional Aid, Total Discount)
   */
  const renderSummaryDetail = (item) => {
    const aidResults = modelConfig.aidResults;
    return (
      <div
        key={item.key}
        className="flex w-full flex-col rounded-md border bg-gray-100 p-3"
      >
        <p className="text-sm text-gray-04">{item.label}</p>
        <p className="text-xl font-semibold text-gray-05">
          {formatFloats(aidResults[item.key], item.unit)}
        </p>
      </div>
    );
  };

  /**
   * Renders navigation buttons for switching between tabs
   * Handles disabled state when operations are in progress
   */
  const renderNavButton = (item) => {
    const isDisabled =
      activeTab === item || Object.values(isSaving).some(Boolean);
    return (
      <CustomButton
        key={item}
        customClass={classNames("!w-full", {
          "bg-white !text-blue-03 disabled:bg-white disabled:!text-blue-03":
            activeTab === item,
        })}
        variant="inverse"
        onClick={() => setActiveTab(item)}
        disabled={isDisabled}
      >
        {item}
      </CustomButton>
    );
  };

  /**
   * Renders the model metadata section containing author and last updated info
   */
  const renderModelMetadata = () => (
    <div className="flex gap-10">{MODEL_DETAILS.map(renderMetadataItem)}</div>
  );

  /**
   * Renders action buttons for export and unpublish functionality
   */
  const renderActionButtons = () => (
    <div className="flex justify-end gap-2">
      <CustomButton
        variant="secondary"
        size="sm"
        onClick={handleUnpublishModelHandler}
        iconBefore={
          <Icons.Edit
            aria-label="Unpublish"
            customClass="h-full w-4 mr-2 text-gray-200 dark:text-gray-600 fill-blue-900"
          />
        }
      >
        Unpublish model
      </CustomButton>
      <CustomButton
        variant="primary"
        size="sm"
        iconBefore={
          isExporting ? (
            <Icons.Spinner customClass="h-full w-4 mr-2 text-gray-200 animate-spin dark:text-gray-600 fill-blue-900" />
          ) : (
            <Icons.Download
              aria-label="Export"
              customClass="h-full w-4 mr-2 text-gray-200 dark:text-gray-600 fill-blue-900"
            />
          )
        }
        onClick={handleExportModel}
        disabled={isExporting}
      >
        {isExporting ? "Exporting..." : "Export"}
      </CustomButton>
    </div>
  );

  /**
   * Renders the model results summary section with key metrics
   */
  const renderModelResults = () => {
    return (
      <div className="mt-4">
        <Heading variant="h3" customClass="font-semibold !text-gray-05">
          Model
        </Heading>
        <div className="mt-1 flex justify-between gap-4">
          {SUMMARY_DETAILS.map(renderSummaryDetail)}
        </div>
      </div>
    );
  };

  /**
   * Renders the active tab content based on user selection
   * Switches between Non-Modeled Aid Results and Admit Adjustments
   */
  const renderContent = () => {
    return activeTab === "Non-Modeled Aid Results" ? (
      <FaoNonModelData />
    ) : (
      <FaoAdmitAdjust />
    );
  };

  return (
    <div className="h-full">
      <ToastContainer />
      {/* Back navigation button */}
      <CustomButton
        variant="text"
        customClass="!p-0 hover:!text-blue-03 !text-blue-02 transition-all"
        iconBefore={
          <Icons.Arrow aria-label="Back" customClass="h-full rotate-180 w-4" />
        }
        onClick={() => navigate("../fao-builder/fao-edit")}
      >
        Back to published models
      </CustomButton>

      {/* Show loader while any data is loading */}
      {Object.values(loading).some(Boolean) ? (
        <Loader />
      ) : (
        <>
          {/* Model metadata and action buttons */}
          {renderModelMetadata()}
          {renderActionButtons()}

          {/* Main results heading */}
          <Heading variant="h2" customClass="font-semibold">
            Aid Results
          </Heading>

          {/* Cost of Attendance table */}
          <AidResultsTable
            displayData={{
              tableHeaders: COST_OF_ATTENDANCE_TABLE_HEADERS,
              tableData: costOfAttendance,
              heading: "Cost of Attendance",
            }}
          />

          {/* First Year Estimate table */}
          <AidResultsTable
            displayData={{
              tableHeaders: TABLE_HEADERS,
              tableData: firstYearEstimate,
              heading: "Total first year estimate",
            }}
          />

          {/* Model results summary */}
          {renderModelResults()}

          {/* Tab navigation for interactive sections */}
          <div className="mt-3 flex justify-between">
            {NAV_BAR_ITEMS.map(renderNavButton)}
          </div>

          {/* Active tab content - no scroll container */}
          <div className="mt-4">{renderContent()}</div>
        </>
      )}
    </div>
  );
}

export default FaoViewResults;
